import { Plus } from 'lucide-react';
import { Link } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import { useLicencias, VISTAS_LICENCIAS, vistaLicencias } from './queries';

const CLAVES = ['q', 'vista'] as const;

function Lista() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const puedeEditar = useCan('tienda', 'editar');
  const vista = vistaLicencias(filtros.vista);
  const q = useLicencias({ ...(filtros.q && { q: filtros.q }), view: vista });
  const filas = q.data?.filas;
  return (
    <>
      <Encabezado
        titulo="Licencias"
        descripcion="Catálogo de la tienda: precios, permisos, obsequios y precios por equipo."
        acciones={
          puedeEditar && (
            <Link to="/tienda/licencias/nueva" className="btn-cs btn-primario">
              <Plus size={16} aria-hidden /> Nueva licencia
            </Link>
          )
        }
      />
      <div role="tablist" aria-label="Tipo de licencia" className="mb-4 flex flex-wrap gap-2">
        {VISTAS_LICENCIAS.map(([k, texto]) => {
          const n = q.data?.conteos[k];
          return (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={vista === k}
              className={cn('btn-cs', vista === k ? 'btn-primario' : 'btn-vidrio')}
              onClick={() => cambiar({ vista: k === 'active' ? '' : k })}
            >
              {texto}
              {n !== undefined && <span className="tabular ml-1 opacity-75">({n})</span>}
            </button>
          );
        })}
      </div>
      <div className="mb-4 flex flex-wrap gap-3">
        <input
          className="input-vidrio max-w-xs"
          placeholder="Nombre o código"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
      </div>
      {vista === 'instructor' && (
        <p className="mb-4 text-sm text-texto-3">
          Dan acceso a instructores a contenidos específicos para validarlos. No se venden en la
          tienda.
        </p>
      )}
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {filas?.length === 0 && <Vacio titulo="Sin licencias con esos filtros" />}
      {filas && filas.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Licencia</th>
                <th>Tipo</th>
                <th>Precio</th>
                <th>Precio final</th>
                <th>Alumnos vigentes</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((l) => (
                <tr key={l.id}>
                  <td className="tabular">{l.sortOrder}</td>
                  <td>
                    <Link to={`/tienda/licencias/${l.id}`} className="font-bold">
                      {l.name}
                    </Link>
                    {l.code && (
                      <span className="ml-2 font-mono text-xs text-texto-3">{l.code}</span>
                    )}
                  </td>
                  <td>{l.type}</td>
                  <td className="tabular">{mxn(l.price)}</td>
                  <td className="tabular font-bold">
                    {mxn(l.finalPrice)}
                    {Number(l.discount) > 0 && (
                      <span className="ml-1 text-xs text-verde-texto">−{Number(l.discount)}%</span>
                    )}
                  </td>
                  <td className="tabular">
                    <Link to={`/alumnos/por-licencia?membership=${l.id}`}>{l.activeUsers}</Link>
                  </td>
                  <td className="space-x-1 whitespace-nowrap">
                    {l.isActive ? (
                      <span className="insignia insignia-activa">Activa</span>
                    ) : (
                      <span className="insignia">Inactiva</span>
                    )}
                    {l.isInstructor && (
                      <span className="insignia insignia-exclusivo">Instructores</span>
                    )}
                    {l.isVisibleInStore && !l.isInstructor && (
                      <span className="insignia">En tienda</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function LicenciasPage() {
  return (
    <PermissionGate seccion="tienda">
      <Lista />
    </PermissionGate>
  );
}
